import { Video } from "lucide-react";

import type { AgendaDay, AgendaEvent } from "@/api/db/agenda";
import type { AgendaStatusValue } from "@/api/db/connection";
import { cn } from "@/lib/utils";
import { AgendaLabels } from "@/lib/labels/agenda";
import { djs } from "@/lib/djs";

import { AgendaView } from "./agenda-view";

function isNotable(status: AgendaStatusValue) {
	if (status === "agendado") {
		return false;
	}

	if (status === "confirmado") {
		return false;
	}

	return true;
}

function EventItem({
	event,
	onSelect,
}: {
	event: AgendaEvent;
	onSelect: (event: AgendaEvent) => void;
}) {
	return (
		<button
			type="button"
			onClick={() => onSelect(event)}
			className="flex min-h-11 w-full items-start gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-border/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
		>
			<span
				className={cn("mt-1.5 size-2 shrink-0 rounded-full", AgendaLabels.tipoDot[event.tipo])}
			/>
			<span className="min-w-0 flex-1">
				<span className="flex min-w-0 items-start gap-2">
					<span className="shrink-0 font-mono text-xs text-muted-foreground">
						{event.start_time}
					</span>
					<span className="min-w-0 break-words text-sm font-medium text-foreground">
						{AgendaView.eventTitle(event)}
					</span>
					{event.telemedicina && <Video className="mt-0.5 size-3.5 shrink-0 text-info" />}
				</span>
				<span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
					<span>{AgendaLabels.tipo[event.tipo]}</span>
					{isNotable(event.status) && (
						<span
							className={cn(
								"rounded-md border px-1.5 py-0.5 font-medium leading-none",
								AgendaLabels.statusChip[event.status],
							)}
						>
							{AgendaLabels.status[event.status]}
						</span>
					)}
				</span>
			</span>
		</button>
	);
}

function DaySection({
	title,
	events,
	onSelect,
	className,
}: {
	title: string;
	events: AgendaEvent[];
	onSelect: (event: AgendaEvent) => void;
	className?: string;
}) {
	return (
		<section aria-label={title} className={cn("min-w-0 p-4", className)}>
			<header className="mb-2 flex items-baseline gap-2">
				<h2 className="type-section-title text-foreground">{title}</h2>
				<span className="rounded-md bg-border/60 px-1.5 py-0.5 font-mono text-xs leading-none text-muted-foreground">
					{events.length}
				</span>
			</header>

			{events.length === 0 && (
				<p className="px-2 py-1 text-xs text-muted-foreground">{AgendaLabels.emptyDay}</p>
			)}

			{events.length > 0 && (
				<div className="flex flex-col">
					{events.map((event) => (
						<EventItem key={event.hash} event={event} onSelect={onSelect} />
					))}
				</div>
			)}
		</section>
	);
}

export function AgendaTodayStrip({
	days,
	today,
	onSelect,
}: {
	days: AgendaDay[];
	today: string;
	onSelect: (event: AgendaEvent) => void;
}) {
	const tomorrow = djs(today, "YYYY-MM-DD").add(1, "day").format("YYYY-MM-DD");

	const byDate = new Map(
		days.map((day) => [
			day.date,
			day.events.filter((event) => !AgendaView.isCancelled(event.status)),
		]),
	);

	return (
		<div className="workspace-frame grid grid-cols-1 overflow-hidden">
			<DaySection title="Hoje" events={byDate.get(today) ?? []} onSelect={onSelect} />
			<DaySection
				title="Amanhã"
				events={byDate.get(tomorrow) ?? []}
				onSelect={onSelect}
				className="border-t border-border"
			/>
		</div>
	);
}
